import { FC, useEffect, useRef, useState } from 'react';
import useRecaptcha from 'hooks/useRecaptcha';
import {
  getFlights,
  getHotels,
  prebuildToDynamic,
  search,
  searchByToken,
} from 'services/dynamic';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DynamicEventResponse, GrossAndTax } from 'ts/dynamic/interfaces/DynamicResponse';
import DetailsDynamicSkeleton from './DetailsDynamicSkeleton';
import { useTranslation } from 'react-i18next';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import ExpiredToken from 'components/shared/ExpiredToken';
import * as idb from 'utils/shared/idbUrl';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import DynamicDetails from './DynamicDetails';
import { SearchDynamicAMModel, SearchDynamicModel } from 'ts/dynamic/classes/SearchDynamicModel';
import { DynamicContext } from 'contexts/DynamicContext';
import { getSortedFlights } from 'utils/dynamic/dynamic';
import { getSortedHotels, initSingleHotel } from 'utils/dynamic/dynamic-htl';
import { b2b } from 'constants/generic';

const DetailsDynamicPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const [urlParams, setUrlParams] = useSearchParams();
  const [data, setData] = useState<DynamicEventResponse>();
  const [updatePage, setUpdatePage] = useState('');
  const [currency, setCurrency] = useState<keyof GrossAndTax>('usd');
  const abortController = useRef(new AbortController());
  const timeoutFlightRef = useRef<any>();
  const timeoutHotelRef = useRef<any>();
  const [numberOfPax, setNumberOfPax] = useState(1);
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  const stateRef = useRef<any>();
  stateRef.current = {
    data,
    currency,
    numberOfPax,
  };

  const matrixConfig = urlParams.get('matrixConfig')?.toLowerCase() || '';
  let storePath = '';
  if (matrixConfig === 'dynamic_fmhmsm') {
    storePath = '/pages/sport';
  } else if (urlParams.get('searchOption') === 'Events') {
    storePath = '/dynamic?MatrixConfig=Dynamic_HMSM';
  } else {
    storePath = '/dynamic?MatrixConfig=' + matrixConfig;
  }
  const { onClickSearch } = usePageInMemory(idbKey, () => saveDynamicPageState());
  const { pathname } = useLocation();

  useGetPage('Dynamic/Details');

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      abortController.current.abort();
      clearTimeout(timeoutFlightRef.current);
      clearTimeout(timeoutHotelRef.current);
    }; // eslint-disable-line
  }, []);

  useEffect(() => {
    if (b2b && pathname !== '/dynamic/details') {
      return;
    }
    if (!isRecaptchaReady) return;
    (async () => {
      const pageState = await idb.getPageState(idbKey);

      if (pageState && pageState.data) {
        setData(pageState.data);
        setCurrency(pageState.currency);
        setNumberOfPax(pageState.numberOfPax);
        idb.deletePageState(idbKey);
        return;
      }
      const token = await handleRecapcha();
      if (!token) return;

      const isDynamicAm = urlParams.get('matrixConfig') === 'Dynamic_AM';

      const model = isDynamicAm
        ? new SearchDynamicAMModel(urlParams)
        : new SearchDynamicModel(urlParams);

      if (urlParams.has('itemNumber')) {
        let searchToken = urlParams.get('searchToken') || '';
        if (!searchToken) {
          const response = await prebuildToDynamic(token, +urlParams.get('itemNumber')!);
          const oldParams = new URLSearchParams(urlParams);
          if (response && !response.hasErrors) {
            searchToken = response.searchToken;
            oldParams.set('searchToken', response.searchToken);
            setUrlParams(oldParams.toString());
          }
        }

        const responseSearch = await searchByToken(token, searchToken, 816);

        if (responseSearch) {
          const newData = {
            ...responseSearch,
          };
          if (newData.dynamicHotel) {
            const defaultSort = 'priceIncrease';
            const sort = sessionStorage.getItem('sorterHotels') || defaultSort;
            newData.dynamicHotel.allHotels = getSortedHotels(newData.dynamicHotel.allHotels, sort);
          }
          if (newData.dynamicFlight) {
            const defaultSort = 'priceIncrease';
            const sort = sessionStorage.getItem('sorterDynFlight') || defaultSort;
            newData.dynamicFlight.allFlights = getSortedFlights(
              newData.dynamicFlight.allFlights,
              sort
            );
          }
          setData(newData);

          if (responseSearch.packageInfo?.currencyCode) {
            setCurrency(responseSearch.packageInfo.currencyCode.toLowerCase() as keyof GrossAndTax);
          }
          if (responseSearch.dynamicFlight?.selectedFlight) {
            const numberPax = responseSearch.dynamicFlight.selectedFlight.flightPricesByPDT.reduce(
              (acc, curr) => curr.quantity + acc,
              0
            );
            setNumberOfPax(responseSearch.paxCount || numberPax);
          }
        }
      } else {
        const response = await search({ ...model, recaptchaToken: token }, abortController.current);

        if (response) {
          const oldParams = new URLSearchParams(urlParams);
          if (response.sessionExpired) {
            oldParams.delete('searchToken');
          } else {
            oldParams.set('searchToken', response.searchToken);
          }
          setUrlParams(oldParams.toString());

          if (response.dynamicHotel?.selectedHotel) {
            const initedHotel = await initSingleHotel(
              response.dynamicHotel.selectedHotel,
              urlParams
            );
            setData({
              ...response,
              dynamicHotel: {
                ...response.dynamicHotel,
                selectedHotel: initedHotel,
              },
            });
          } else {
            setData(response);
          }
          if (response.dynamicFlight?.selectedFlight) {
            const numberPax = response.dynamicFlight.selectedFlight.flightPricesByPDT.reduce(
              (acc, curr) => curr.quantity + acc,
              0
            );
            setNumberOfPax(numberPax);
          }

          if (response.packageInfo?.currencyCode) {
            setCurrency(response.packageInfo.currencyCode.toLowerCase() as keyof GrossAndTax);
          }
          await initFlights(response.dynamicFlight);
          await initHotels(response.dynamicHotel, response.sysToken);
        }
      }
    })();
  }, [isRecaptchaReady, updatePage, urlParams.get('searchOption')]); // eslint-disable-line

  const initHotels = async (dynHotel: DynamicEventResponse['dynamicHotel'], sysToken: string) => {
    if (dynHotel?.searchToken) {
      const hotelResponse = await getHotels(
        dynHotel.searchToken,
        sysToken,
        abortController.current
      );

      if (hotelResponse) {
        const sort = 'priceIncrease';
        setData((prev) => {
          if (prev && prev.dynamicHotel) {
            const allHotels = [...prev.dynamicHotel.allHotels, ...hotelResponse.hotels].filter(
              (hot, ind, arr) => {
                return arr.findIndex((h) => h.itemCode === hot.itemCode) === ind;
              }
            );

            const newHotels = getSortedHotels(allHotels, sort);
            const newResult = {
              ...prev,
              dynamicHotel: {
                ...prev.dynamicHotel,
                allHotels: newHotels,
              },
            };

            if (!hotelResponse.poolingFinished) {
              timeoutHotelRef.current = setTimeout(() => initHotels(newResult.dynamicHotel, sysToken), 4000);
            }

            return newResult;
          } else {
            return prev;
          }
        });
      }
    }
  };

  const initFlights = async (dynFlight: DynamicEventResponse['dynamicFlight']) => {
    if (dynFlight?.searchToken) {
      const flightsResponse = await getFlights(
        dynFlight.searchToken,
        dynFlight.allFlights.length,
        abortController.current
      );

      if (flightsResponse) {
        const sort = 'priceIncrease';
        setData((prev) => {
          if (prev && prev.dynamicFlight) {
            const newFlights = getSortedFlights(
              [...prev.dynamicFlight.allFlights, ...flightsResponse.flights],
              sort
            );
            const newResult = {
              ...prev,
              dynamicFlight: {
                ...prev.dynamicFlight,
                allFlights: newFlights,
              },
            };

            if (!flightsResponse.poolingFinished) {
              timeoutFlightRef.current = setTimeout(
                () => initFlights(newResult.dynamicFlight),
                4000
              );
            }

            return newResult;
          } else {
            return prev;
          }
        });
      }
    }
  };

  const saveDynamicPageState = () => {
    idb.savePageState(idbKey, stateRef.current);
  };

  return (
    <DynamicContext.Provider value={{ currency, setCurrency, numberOfPax, setNumberOfPax }}>
      {!!data &&
        !data.hasErrors &&
        !data.sessionExpired &&
        !data.attractionNotFound &&
        !data.hotelNotFound &&
        !data.flightNotFound && (
          <DynamicDetails
            data={data}
            onChangeData={setData}
            onUpdate={(str) => setUpdatePage(new Date().getTime().toString() + (str ? str : ''))}
            onSuccessBook={saveDynamicPageState}
            onClickBack={() => navigate(storePath)}
            onClickSearch={() => onClickSearch(matrixConfig, storePath)}
          />
        )}
      {!data && <DetailsDynamicSkeleton />}
      {data && data.sessionExpired && <ExpiredToken storePath={storePath} />}

      {!!data &&
        (data.hotelNotFound ||
          data.attractionNotFound ||
          data.flightNotFound ||
          data.hasErrors) && (
          <div className="expired">
            <div className="title mb-10">{t('dyn.no_avail')}</div>
            {data.hotelNotFound && <div className="mb-10">{t('dyn.hotel_not_found')}</div>}
            {data.flightNotFound && <div className="mb-10">{t('dyn.flight_not_found')}</div>}
            {data.attractionNotFound && (
              <div className="mb-10">{t('dyn.attraction_not_found')}</div>
            )}
            {data.hasErrors && <div className="mb-10">{t('dyn.error')}</div>}
            <Button
              title={t('dyn.search_again')}
              onClick={() => navigate('/dynamic?MatrixConfig=Dynamic_FMHMSM')}
              icon={faMagnifyingGlass}
              iconclassname="mr-5"
            />
          </div>
        )}
    </DynamicContext.Provider>
  );
};

export default withErrorBoundary(DetailsDynamicPage, 'DetailsDynamicPage');
